import React, { useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import moment from "moment";

import { Button } from "src/app/components/Button/Button";
import UsernameAndAvatar from "src/app/components/UsernameAndAvatar/UsernameAndAvatar";
import { CustomTable } from "src/app/components/CustomTable/CustomTable";
import CustomCheckbox from "src/app/components/CustomCheckbox/CustomCheckbox";
import { SelectedTasksBar } from "src/app/components/SelectedTasksBar/SelectedTasksBar";
import { RemoveModal } from "src/app/modals/RemoveModal/RemoveModal";
import { API_URLS } from "src/utils/API_URLS";
import { getDashboardList } from "src/redux/insights-dashboard/insightsDashboardActions";
import { openAIChatWithContext } from "src/redux/ai-chat/aiChatActions";
import { DropdownMenuItem } from "src/app/components/Dropdown/Dropdown";
import { DropdownPortalMenu } from "src/app/components/DropdownPortal";
import { DropdownPopper } from "src/app/components/DropdownPopper/DropdownPopper";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { RootState } from "src/redux/reducers";
import { SocialInsightsDashboardListElement } from "src/redux/insights-dashboard/utils";
import { WorkspacePublicationsType } from "src/redux/insights-dashboard/types";
import { WorkspacePermissionsEnum } from "src/utils/PermissionsEnums";
import { getRoute, ROUTES } from "src/utils/ROUTES";
import { wsAxiosDelete } from "src/helpers/wsAxios";
import { showErrorToast } from "src/utils/methods";
import { RenameDashboardModal } from "../SocialInsightsDashboardManagement/modals/RenameDashboardModal/RenameDashboardModal";
import { RemoveDashboardModal } from "../SocialInsightsDashboardManagement/modals/RemoveDashboardModal/RemoveDashboardModal";
import { DuplicateDashboardModal } from "../SocialInsightsDashboardManagement/modals/DuplicateDashboardModal/DuplicateDashboardModal";
import {
  setIDDates,
  setSelectedDashboard,
  setSetup,
  setShowTrends,
} from "src/redux";
import {
  getFiltersFromSetup,
  hydrateInsightsDashboardDateRange,
  hydrateInsightsDashboardSetup,
} from "../../utils";
import { SOCIAL_ICONS, SOCIAL_LABELS } from "src/types/generic";
import { ReactComponent as EditIcon } from "src/images/edit.svg";
import { ReactComponent as TrashCanGlass } from "src/images/trash-can-gray.svg";
import { ReactComponent as DuplicateIcon } from "src/images/duplicate.svg";
import { ReactComponent as DotsIcon } from "src/images/dots.svg";
import { ReactComponent as AIStarsIcon } from "src/images/ai-stars.svg";
import { ReactComponent as RubberIcon } from "src/images/rubber.svg";

import "./SocialInsightsDashboardList.scss";

const VISIBLE_CHIPS_LIMIT = 4;

const renderOverflowChips = (
  values: string[],
  renderValue: (value: string) => React.ReactNode,
  variant: "outlined" | "plain" = "outlined",
) => {
  if (!values.length) {
    return <span className="social-insights-dashboard-list__muted">—</span>;
  }

  const visible = values.slice(0, VISIBLE_CHIPS_LIMIT);
  const overflowCount = values.length - visible.length;

  const chipClassName =
    variant === "plain"
      ? "social-insights-dashboard-list__chip social-insights-dashboard-list__chip--plain"
      : "social-insights-dashboard-list__chip";

  return (
    <div className="social-insights-dashboard-list__chips">
      {visible.map((value) => (
        <span key={value} className={chipClassName}>
          {renderValue(value)}
        </span>
      ))}
      {overflowCount > 0 && (
        <span
          className={`${chipClassName} social-insights-dashboard-list__chip--overflow`}
        >
          +{overflowCount}
        </span>
      )}
    </div>
  );
};

interface Row {
  uuid: string;
  select: React.ReactNode;
  name: React.ReactNode;
  hashtags: React.ReactNode;
  mentions: React.ReactNode;
  countries: React.ReactNode;
  platform: React.ReactNode;
  insightScope: React.ReactNode;
  timeRange: React.ReactNode;
  createdBy: React.ReactNode;
  createdAt: React.ReactNode;
  actions?: React.ReactNode;
  onRowClick: () => void;
}

const openDashboardActionsDropdownRef: {
  current: { forceVisible: (state: boolean) => void } | null;
} = { current: null };

const DashboardActionsDropdown = ({
  onRename,
  onDelete,
  onDuplicate,
}: {
  onRename?: () => void;
  onDelete?: () => void;
  onDuplicate: () => void;
}) => {
  const dropdownRef = useRef<{ forceVisible: (state: boolean) => void }>(null);

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <DropdownPopper
        ref={dropdownRef}
        placement="bottom-end"
        overlay={
          <DropdownPortalMenu
            onClick={() => dropdownRef.current?.forceVisible(false)}
          >
            {onRename && (
              <DropdownMenuItem onClick={onRename}>
                <EditIcon />
                <IDHFormattedMessage id="ws_rename" defaultMessage="Rename" />
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onDuplicate}>
              <DuplicateIcon />
              <IDHFormattedMessage
                id="ws_duplicate"
                defaultMessage="Duplicate"
              />
            </DropdownMenuItem>
            {onDelete && (
              <DropdownMenuItem
                className="dropdown__menu-item--danger"
                onClick={onDelete}
              >
                <TrashCanGlass />
                <IDHFormattedMessage id="ws_delete" defaultMessage="Delete" />
              </DropdownMenuItem>
            )}
          </DropdownPortalMenu>
        }
      >
        <Button
          size="square"
          variant="grey"
          onClick={() => {
            if (
              openDashboardActionsDropdownRef.current &&
              openDashboardActionsDropdownRef.current !== dropdownRef.current
            ) {
              openDashboardActionsDropdownRef.current.forceVisible(false);
            }
            openDashboardActionsDropdownRef.current = dropdownRef.current;
          }}
        >
          <DotsIcon />
        </Button>
      </DropdownPopper>
    </span>
  );
};

export const SocialInsightsDashboardList = () => {
  const [selectedDashboardUuids, setSelectedDashboardUuids] = useState<
    string[]
  >([]);
  const [showBulkRemoveModal, setShowBulkRemoveModal] = useState(false);
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [dashboardToRename, setDashboardToRename] =
    useState<SocialInsightsDashboardListElement | null>(null);
  const [dashboardToDelete, setDashboardToDelete] =
    useState<SocialInsightsDashboardListElement | null>(null);
  const [dashboardToDuplicate, setDashboardToDuplicate] =
    useState<SocialInsightsDashboardListElement | null>(null);

  const history = useHistory();
  const { search } = useLocation();
  const dispatch = useDispatch();

  const { dashboardList, setupConfig, dateRanges } = useSelector(
    (state: RootState) => state.insightsDashboardReducer,
  );
  const { activeWorkspaceUuid, identity } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const loggedInMemberUuid = identity?.id ?? null;
  const canManageAllDashboards = Boolean(
    identity?.permissions?.workspace?.includes(
      WorkspacePermissionsEnum.MANAGE_ALL_SOCIAL_INSIGHTS_DASHBOARD,
    ),
  );

  const canManageDashboard = (
    dashboard: SocialInsightsDashboardListElement,
  ) => {
    if (canManageAllDashboards) return true;
    if (!loggedInMemberUuid) return false;

    return dashboard.ownerWsMemberUuid === loggedInMemberUuid;
  };

  const openDashboard = (dashboard: SocialInsightsDashboardListElement) => {
    // setupConfig may still be loading (fetched when the list mounted); hydrating
    // against it here would freeze broken/empty filters and set selectedDashboard,
    // which stops the detail page's own hydration effect from ever running.
    // When it's not ready yet, just navigate and let that effect do the hydration.
    if (setupConfig) {
      const hydratedSetup = hydrateInsightsDashboardSetup(
        dashboard.filters,
        setupConfig,
      );
      const hydratedDateRange = hydrateInsightsDashboardDateRange(
        dashboard.filters.dateRange,
        dateRanges,
      );

      const { dateFrom, dateTo } = dashboard.filters;

      dispatch(setIDDates({ dateFrom, dateTo, dateRange: hydratedDateRange }));
      // showTrends is a local-only UI preference now — always starts off.
      dispatch(setShowTrends(false));
      dispatch(setSelectedDashboard(dashboard));
      dispatch(setSetup(hydratedSetup));
    }

    history.push({
      pathname: getRoute(ROUTES.SOCIAL_INSIGHTS_DASHBOARD, {
        wsWorkspaceUuid: activeWorkspaceUuid,
        wsDashboardUuid: dashboard.uuid,
      }),
      search,
    });
  };

  const toggleDashboardSelection = (dashboardUuid: string) => {
    setSelectedDashboardUuids((selected) =>
      selected.includes(dashboardUuid)
        ? selected.filter((uuid) => uuid !== dashboardUuid)
        : [...selected, dashboardUuid],
    );
  };

  const toggleAllDashboardSelection = () => {
    setSelectedDashboardUuids((selected) =>
      selected.length === dashboardList.length
        ? []
        : dashboardList.map((dashboard) => dashboard.uuid),
    );
  };

  const selectedDashboards = dashboardList.filter((dashboard) =>
    selectedDashboardUuids.includes(dashboard.uuid),
  );

  const removeSelectedDashboards = async () => {
    setBulkRemoving(true);
    await Promise.all(
      selectedDashboards.map((dashboard) =>
        wsAxiosDelete({
          url: API_URLS.deleteInsightsDashboard
            .replace(":workspaceUuid:", activeWorkspaceUuid)
            .replace(":dashboardUuid:", dashboard.uuid),
          errorCallback: () => showErrorToast(),
        }),
      ),
    );
    dispatch(getDashboardList(activeWorkspaceUuid));
    setSelectedDashboardUuids([]);
    setBulkRemoving(false);
  };

  const columns = [
    {
      Header: (
        <CustomCheckbox
          id="select-all-dashboards"
          checked={
            dashboardList.length > 0 &&
            selectedDashboardUuids.length === dashboardList.length
          }
          onChange={toggleAllDashboardSelection}
          blue
        />
      ),
      accessor: "select",
      width: 44,
      sticky: "left",
    },
    {
      Header: "Dashboard",
      accessor: "name",
      width: 260,
      minWidth: 200,
      sticky: "left",
    },
    {
      Header: "Hashtags",
      accessor: "hashtags",
      width: 160,
      minWidth: 150,
    },
    {
      Header: "Mentions",
      accessor: "mentions",
      width: 160,
      minWidth: 150,
    },
    {
      Header: "Countries",
      accessor: "countries",
      width: 260,
      minWidth: 150,
    },
    {
      Header: "Platform",
      accessor: "platform",
      width: 300,
      minWidth: 150,
    },
    {
      Header: "Insight scope",
      accessor: "insightScope",
      width: 160,
      minWidth: 150,
    },
    {
      Header: "Time range",
      accessor: "timeRange",
      width: 160,
      minWidth: 150,
    },
    {
      Header: "Created by",
      accessor: "createdBy",
      width: 200,
      minWidth: 150,
    },
    {
      Header: "Created",
      accessor: "createdAt",
      width: 140,
      minWidth: 150,
    },
    {
      Header: "",
      accessor: "actions",
      width: 44,
      sticky: "right",
    },
  ];

  const rows: Row[] = (dashboardList ?? []).map((dashboard, index) => {
    const setup = hydrateInsightsDashboardSetup(dashboard.filters, setupConfig);
    const resolvedFilters = getFiltersFromSetup(setup, setupConfig);
    // getFiltersFromSetup fills in every available country/platform when the
    // setup has none selected (that's "all" for querying purposes) — for
    // display we need to tell that apart from an actual explicit selection.
    const hasCountriesSelected = Boolean(setup?.countries?.length);
    const hasPlatformsSelected = Boolean(setup?.platforms?.length);
    const resolvedCountries = resolvedFilters.countries;
    const resolvedPlatforms = resolvedFilters.platforms;
    const resolvedHashtags = setup?.hashtags ?? [];
    const resolvedMentions = setup?.mentions ?? [];
    const canManage = canManageDashboard(dashboard);

    const insightScope =
      setup?.workspacePublicationsType === WorkspacePublicationsType.All ? (
        <IDHFormattedMessage
          id="insight_scope_market_view"
          defaultMessage="Market view"
        />
      ) : setup?.workspacePublicationsType ===
        WorkspacePublicationsType.WorkspaceOnly ? (
        <IDHFormattedMessage
          id="insight_scope_my_workspace"
          defaultMessage="My workspace"
        />
      ) : (
        <span className="social-insights-dashboard-list__muted">—</span>
      );

    const hydratedDateRange = hydrateInsightsDashboardDateRange(
      dashboard.filters.dateRange,
      dateRanges,
    );
    const { dateFrom, dateTo } = dashboard.filters;
    const timeRange =
      hydratedDateRange?.value === "custom" && dateFrom && dateTo
        ? `${dateFrom} - ${dateTo}`
        : hydratedDateRange?.label ?? (
            <span className="social-insights-dashboard-list__muted">—</span>
          );

    return {
      uuid: dashboard.uuid,
      select: (
        <span
          className={classNames("social-insights-dashboard-list__select", {
            "social-insights-dashboard-list__select--checked":
              selectedDashboardUuids.includes(dashboard.uuid),
          })}
        >
          <span className="social-insights-dashboard-list__row-index">
            {index + 1}
          </span>
          <span
            className="social-insights-dashboard-list__checkbox"
            onClick={(event) => event.stopPropagation()}
          >
            <CustomCheckbox
              id={`select-dashboard-${dashboard.uuid}`}
              checked={selectedDashboardUuids.includes(dashboard.uuid)}
              onChange={() => toggleDashboardSelection(dashboard.uuid)}
              blue
            />
          </span>
        </span>
      ),
      name: (
        <span
          className="social-insights-dashboard-list__name-cell social-insights-dashboard-list__name-cell--clickable"
          onClick={(event) => {
            event.stopPropagation();
            openDashboard(dashboard);
          }}
        >
          {dashboard.name}
        </span>
      ),
      hashtags: renderOverflowChips(
        resolvedHashtags,
        (hashtag) => <>#{hashtag}</>,
        "plain",
      ),
      mentions: renderOverflowChips(
        resolvedMentions,
        (mention) => <>@{mention}</>,
        "plain",
      ),
      countries: hasCountriesSelected ? (
        renderOverflowChips(
          resolvedCountries.map((country) => country.label),
          (label) => label,
        )
      ) : (
        <IDHFormattedMessage
          id="all_countries"
          defaultMessage="All countries"
        />
      ),
      platform: !hasPlatformsSelected ? (
        <IDHFormattedMessage
          id="all_platforms"
          defaultMessage="All platforms"
        />
      ) : (
        renderOverflowChips(resolvedPlatforms, (platform) => (
          <>
            <img
              className="social-insights-dashboard-list__platform-icon"
              src={SOCIAL_ICONS[platform]}
              alt={platform}
            />
            {SOCIAL_LABELS[platform] || platform}
          </>
        ))
      ),
      insightScope,
      timeRange,
      createdBy: dashboard.ownerName ? (
        <UsernameAndAvatar
          username={dashboard.ownerName}
          avatarUrl={dashboard.ownerAvatarUrl ?? ""}
          tooltipMaxWidth={160}
        />
      ) : (
        <span className="social-insights-dashboard-list__muted">—</span>
      ),
      createdAt: dashboard.createdAt ? (
        moment(dashboard.createdAt).format("DD.MM.YYYY")
      ) : (
        <span className="social-insights-dashboard-list__muted">—</span>
      ),
      actions: (
        <DashboardActionsDropdown
          onRename={
            canManage ? () => setDashboardToRename(dashboard) : undefined
          }
          onDelete={
            canManage ? () => setDashboardToDelete(dashboard) : undefined
          }
          onDuplicate={() => setDashboardToDuplicate(dashboard)}
        />
      ),
    };
  });

  return (
    <div className="social-insights-dashboard-list">
      <div className="social-insights-dashboard-list__table-wrapper">
        <CustomTable
          // @ts-ignore -- Row cells hold ReactNode content, not the strict Column shape CustomTable's TS types were written for
          columns={columns}
          data={rows}
          rowClassName="social-insights-dashboard-list__row"
          getRowClassName={(row) =>
            selectedDashboardUuids.includes(row.uuid)
              ? "social-insights-dashboard-list__row--selected"
              : undefined
          }
        />
      </div>

      {selectedDashboardUuids.length > 0 && (
        <div className="social-insights-dashboard-list__group-actions">
          <SelectedTasksBar selectedTasksLength={selectedDashboardUuids.length}>
            <Button
              size="large"
              variant="white"
              dataQa="discuss-with-ai"
              onClick={() =>
                dispatch(
                  openAIChatWithContext({
                    type: "selectedSocialInsightsDashboards",
                    data: selectedDashboards.map((dashboard) => ({
                      uuid: dashboard.uuid,
                      title: dashboard.name,
                    })),
                  }),
                )
              }
            >
              <AIStarsIcon />
              <IDHFormattedMessage
                id="ws_discuss_with_ai_assistant"
                defaultMessage="Discuss with AI Assistant"
              />
            </Button>
            <Button
              size="large"
              variant="white"
              dataQa="clear-selected-dashboards"
              onClick={() => setSelectedDashboardUuids([])}
            >
              <RubberIcon />
              <IDHFormattedMessage
                id="ws_clear_selected"
                defaultMessage="Clear"
              />
            </Button>
            <Button
              size="large"
              variant="white"
              className="social-insights-dashboard-list__remove-selected"
              dataQa="remove-selected-dashboards"
              onClick={() => setShowBulkRemoveModal(true)}
            >
              <TrashCanGlass />
              <IDHFormattedMessage id="ws_remove" defaultMessage="Remove" />
            </Button>
          </SelectedTasksBar>
        </div>
      )}

      {showBulkRemoveModal && (
        <RemoveModal
          onClose={() => setShowBulkRemoveModal(false)}
          objectNames={selectedDashboards.map((dashboard) => dashboard.name)}
          loading={bulkRemoving}
          removeFunction={removeSelectedDashboards}
        />
      )}

      {dashboardToRename && (
        <RenameDashboardModal
          data={dashboardToRename}
          onClose={() => setDashboardToRename(null)}
        />
      )}

      {dashboardToDelete && (
        <RemoveDashboardModal
          data={dashboardToDelete}
          onClose={() => setDashboardToDelete(null)}
        />
      )}

      {dashboardToDuplicate && (
        <DuplicateDashboardModal
          data={dashboardToDuplicate}
          onClose={() => setDashboardToDuplicate(null)}
        />
      )}
    </div>
  );
};
